import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { useToast } from '../components/ui/use-toast';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import { useRestaurant } from '../context/RestaurantContext';
import { confirm } from '../components/ui/confirm-dialog';
import {
  listIngredients, createIngredient, updateIngredient, deleteIngredient,
} from '../services/ingredientService';

const EMPTY = { name: '', description: '', base_unit: '', track_inventory: false, is_veg: true, is_allergen: false, is_active: true };

export default function IngredientsManagement() {
  const { restaurantId } = useRestaurant();
  const { toast } = useToast();
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // null = closed, {} = create, {id, ...} = edit
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const ings = await listIngredients(restaurantId);
      setIngredients(ings);
    } catch (e) {
      toast({ title: 'Error', description: e.error || 'Failed to load', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ingredients;
    return ingredients.filter((i) => i.name.toLowerCase().includes(q));
  }, [ingredients, search]);

  const openCreate = () => setEditing({ ...EMPTY });
  const openEdit = (ing) => setEditing({
    id: ing.id,
    name: ing.name,
    description: ing.description || '',
    base_unit: ing.base_unit || '',
    track_inventory: !!ing.track_inventory,
    is_veg: ing.is_veg !== false,
    is_allergen: !!ing.is_allergen,
    is_active: ing.is_active,
  });

  const handleSave = async () => {
    if (!editing.name?.trim()) {
      toast({ title: 'Name required', description: 'Ingredient name is required.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        description: editing.description || '',
        base_unit: (editing.base_unit || '').trim(),
        track_inventory: !!editing.track_inventory,
        is_veg: !!editing.is_veg,
        is_allergen: !!editing.is_allergen,
        is_active: editing.is_active,
      };
      if (editing.id) {
        await updateIngredient(restaurantId, editing.id, payload);
      } else {
        await createIngredient(restaurantId, payload);
      }
      toast({ title: 'Saved', description: 'Ingredient saved.' });
      setEditing(null);
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.error || 'Failed to save', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ing) => {
    if (!(await confirm({ description: `Delete "${ing.name}"?`, confirmText: 'Delete', tone: 'destructive' }))) return;
    try {
      await deleteIngredient(restaurantId, ing.id);
      toast({ title: 'Deleted', description: `${ing.name} removed.` });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.error || 'Failed to delete', variant: 'destructive' });
    }
  };

  return (
    <div className="w-full p-6 space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">Ingredients</h1>
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>All ingredients</CardTitle>
          </div>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" /> Add Ingredient
          </Button>
        </CardHeader>
        <CardContent>
          <div className="mb-4 relative max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search ingredients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Diet</TableHead>
                  <TableHead>Allergen</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Inventory</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-500">Loading…</TableCell></TableRow>
                )}
                {!loading && filtered.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-500">No ingredients yet.</TableCell></TableRow>
                )}
                {filtered.map((ing) => (
                  <TableRow key={ing.id}>
                    <TableCell className="font-medium">{ing.name}</TableCell>
                    <TableCell>
                      {ing.is_veg === false ? (
                        <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-800">Non-Veg</span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800">Veg</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {ing.is_allergen ? (
                        <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800">Allergen</span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-md truncate">{ing.description || '—'}</TableCell>
                    <TableCell>
                      {ing.track_inventory ? (
                        <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
                          Tracked{ing.base_unit ? ` · ${ing.base_unit}` : ''}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-1 rounded ${ing.is_active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}`}>
                        {ing.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(ing)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(ing)} className="text-red-600 hover:text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit Ingredient' : 'New Ingredient'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="e.g., Milk, Wheat Flour, Peanuts"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Diet</Label>
                  <select
                    value={editing.is_veg ? 'veg' : 'nonveg'}
                    onChange={(e) => setEditing({ ...editing, is_veg: e.target.value === 'veg' })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    <option value="veg">Veg</option>
                    <option value="nonveg">Non-Veg</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Allergen</Label>
                  <label className="flex items-center gap-2 h-10">
                    <input
                      type="checkbox"
                      checked={!!editing.is_allergen}
                      onChange={(e) => setEditing({ ...editing, is_allergen: e.target.checked })}
                      className="h-4 w-4"
                    />
                  </label>
                </div>
              </div>
              <div className="rounded-md border p-3 space-y-2 bg-slate-50">
                <div className="flex items-center gap-2">
                  <input
                    id="track_inventory"
                    type="checkbox"
                    checked={editing.track_inventory}
                    onChange={(e) => setEditing({ ...editing, track_inventory: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="track_inventory">Track this item in Inventory</Label>
                </div>
                {editing.track_inventory && (
                  <div className="space-y-2 pt-1">
                    <Label>Stock Unit</Label>
                    <Input
                      value={editing.base_unit}
                      onChange={(e) => setEditing({ ...editing, base_unit: e.target.value })}
                      placeholder="e.g., kg, litre, units"
                    />
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  rows={2}
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  placeholder="Optional notes"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="is_active"
                  type="checkbox"
                  checked={editing.is_active}
                  onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
